import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "../hooks/useDialogFocus";
export function ConfirmDialog({ open, title, message, confirmLabel = "OK", cancelLabel = "Cancel", variant = "default", onConfirm, onCancel, portalRoot, }) {
    const confirmRef = useRef(null);
    const panelRef = useRef(null);
    // UX-02: shared dialog focus contract — initial focus, Tab loop, Escape and
    // focus restore to the trigger. Replaces the ad-hoc timers above.
    useDialogFocus(panelRef, { open, onEscape: onCancel, initialFocusRef: confirmRef });
    if (!open)
        return null;
    const dialog = (_jsx("div", { style: {
            position: "fixed",
            inset: 0,
            zIndex: 2147483000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
        }, onClick: (e) => { if (e.target === e.currentTarget)
            onCancel(); }, role: "dialog", "aria-modal": "true", "aria-label": title ?? message, children: _jsxs("div", { ref: panelRef, style: {
                // T6: portal lands on document.body (no .vnsc-root) — every
                // var carries a dark fallback (portal law, option B).
                background: "var(--vnsc-bg-modal, #1e222d)",
                color: "var(--vnsc-text-primary, #d1d4dc)",
                borderRadius: "var(--vnsc-radius-modal, 12px)",
                padding: "var(--vnsc-space-modal-padding, 24px)",
                minWidth: 300,
                maxWidth: 420,
                boxShadow: "var(--vnsc-shadow-modal, 0 8px 32px rgba(0,0,0,0.4))",
                display: "flex",
                flexDirection: "column",
                gap: "var(--vnsc-space-4, 16px)",
            }, children: [title && (_jsx("div", { style: { fontSize: "var(--vnsc-font-size-lg, 16px)", fontWeight: 600, lineHeight: 1.4 }, children: title })), _jsx("div", { style: { fontSize: "var(--vnsc-font-size-md, 14px)", lineHeight: 1.5, opacity: 0.9 }, children: message }), _jsxs("div", { style: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }, children: [_jsx("button", { onClick: onCancel, style: {
                                padding: "var(--vnsc-space-2, 8px) var(--vnsc-space-4, 16px)",
                                borderRadius: "var(--vnsc-radius-button, 8px)",
                                border: "1px solid var(--vnsc-border, rgba(255,255,255,0.12))",
                                background: "transparent",
                                color: "var(--vnsc-text-primary, #d1d4dc)",
                                fontSize: "var(--vnsc-font-size-base, 13px)",
                                cursor: "pointer",
                            }, children: cancelLabel }), _jsx("button", { ref: confirmRef, onClick: onConfirm, style: {
                                padding: "var(--vnsc-space-2, 8px) var(--vnsc-space-4, 16px)",
                                borderRadius: "var(--vnsc-radius-button, 8px)",
                                border: "none",
                                background: variant === "danger" ? "var(--vnsc-color-error, #dc2626)" : "var(--vnsc-color-primary, #2563eb)",
                                color: "var(--vnsc-text-on-primary, #fff)",
                                fontSize: "var(--vnsc-font-size-base, 13px)",
                                fontWeight: 600,
                                cursor: "pointer",
                            }, children: confirmLabel })] })] }) }));
    const mountTarget = portalRoot || (typeof document !== "undefined" ? document.body : null);
    return mountTarget ? createPortal(dialog, mountTarget) : null;
}
